import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import MT5Accounts from './pages/MT5Accounts';
import AccountApprovals from './pages/admin/AccountApprovals';
import WalletPage from './pages/WalletPage';
import WalletApprovals from './pages/admin/WalletApprovals';
import ProfitSharingPage from './pages/ProfitSharingPage';
import ProfitSharingSettings from './pages/admin/ProfitSharingSettings';
import ReferralDashboard from './pages/ReferralDashboard';
import ReferralManagement from './pages/admin/ReferralManagement';
import WithdrawalPage from './pages/WithdrawalPage';
import WithdrawalManagement from './pages/admin/WithdrawalManagement';
import AdminDashboard from './pages/admin/AdminDashboard';
import ReportsPage from './pages/admin/ReportsPage';
import UserDashboard from './pages/UserDashboard';
import Navbar from './components/common/Navbar';
import Sidebar from './components/common/Sidebar';
import Card from './components/common/Card';
import Button from './components/common/Button';
import StatusBadge from './components/common/StatusBadge';
import { Cpu, Wallet, TrendingUp, Users, ShieldCheck, CheckCircle2 } from 'lucide-react';

function MainApp() {
  const { user, isAuthenticated, isAdmin, logout, loading } = useAuth();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [authMode, setAuthMode] = useState('landing'); // 'landing' | 'login' | 'register'
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Enforce Light Theme ONLY when logged in. Unauthenticated pages (Landing, Login, Register) always stay in Dark Theme.
  useEffect(() => {
    if (!isAuthenticated) {
      document.documentElement.classList.remove('light-theme');
    } else {
      if (theme === 'light') {
        document.documentElement.classList.add('light-theme');
      } else {
        document.documentElement.classList.remove('light-theme');
      }
    }
  }, [isAuthenticated, theme]);

  // Automatically switch activeTab to 'admin-dashboard' for Admin users
  useEffect(() => {
    if (isAdmin && activeTab === 'dashboard') {
      setActiveTab('admin-dashboard');
    }
  }, [isAdmin, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-[#FFFFFF] p-4">
        <div className="flex flex-col items-center space-y-3">
          <div className="w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-[#9CA3AF]">Authenticating Security Credentials...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authMode === 'login') {
      return <Login onSwitchToRegister={() => setAuthMode('register')} onGoToHome={() => setAuthMode('landing')} />;
    }
    if (authMode === 'register') {
      return <Register onSwitchToLogin={() => setAuthMode('login')} onGoToHome={() => setAuthMode('landing')} />;
    }
    return <LandingPage onGoToLogin={() => setAuthMode('login')} onGoToRegister={() => setAuthMode('register')} />;
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-[#FFFFFF] font-sans antialiased">
      {/* 1. Fixed Top Navbar */}
      <Navbar 
        user={user} 
        onLogout={logout} 
        onToggleMobileSidebar={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        isMobileSidebarOpen={isMobileSidebarOpen}
      />

      <div className="flex min-h-[calc(100vh-4rem)] pt-16">
        {/* 2. Fixed Left Sidebar */}
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          isAdmin={isAdmin} 
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />

        {/* 3. ONLY SCROLLABLE RIGHT CONTENT AREA */}
        <main className="flex-1 lg:ml-64 p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-y-auto">
          {/* Render Tab Content based on Navigation */}
          {activeTab === 'admin-dashboard' ? (
            <AdminDashboard />
          ) : activeTab === 'admin-reports' ? (
            <ReportsPage />
          ) : activeTab === 'mt5' ? (
            <MT5Accounts />
          ) : activeTab === 'admin-mt5' ? (
            <AccountApprovals />
          ) : activeTab === 'wallet' ? (
            <WalletPage />
          ) : activeTab === 'admin-wallet' ? (
            <WalletApprovals />
          ) : activeTab === 'withdrawals' ? (
            <WithdrawalPage />
          ) : activeTab === 'admin-withdrawals' ? (
            <WithdrawalManagement />
          ) : activeTab === 'admin-rates' ? (
            <ProfitSharingSettings />
          ) : activeTab === 'referrals' ? (
            <ReferralDashboard />
          ) : activeTab === 'admin-users' ? (
            <ReferralManagement />
          ) : isAdmin ? (
            <AdminDashboard />
          ) : (
            <UserDashboard user={user} setActiveTab={setActiveTab} />
          )}
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#121215',
              color: '#FFFFFF',
              border: '1px solid #27272A',
              fontSize: '12px',
              borderRadius: '12px',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#121215',
              },
            },
          }}
        />
        <MainApp />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
